import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { SKIP_SPINNER } from 'src/app/core/interceptors/spinner.Interceptor';
import { environment } from 'src/environments/environment';
import { Observable, expand, EMPTY, map, reduce, Subject } from 'rxjs';

export type CalendarProvider = 'google' | 'outlook';
export interface CalendarConnection {
  provider: CalendarProvider;
  configured: boolean;
  color?: string | null;
  status: string;
  pending: boolean;
  calendarIds: string[];
  defaultTag: string | null;
  lastSyncedAt: string | null;
  importedCount: number;
}
export interface ExternalCalendar { id: string; name: string; }
export interface CalendarCompany { id: string; name: string; }
export interface CalendarRecordedTime { id:string; startTime:string; duration:number; status:string; description:string; }
export interface CalendarEvent {
  recordedTimes?: CalendarRecordedTime[];
  id: string;
  provider: CalendarProvider;
  sourceTag: string;
  defaultTag: string | null;
  title: string;
  description: string;
  location: string;
  sourceStartsAt?: string;
  sourceEndsAt?: string;
  startsAt: string;
  endsAt: string;
  participants: Array<{ email: string; name: string }>;
  organizer: { email?: string; name?: string };
  sourceUrl: string | null;
  meetingUrl: string | null;
  sourceStatus: 'confirmed' | 'deleted';
  status: string;
  allDay: boolean;
  isPrivate: boolean;
  company: CalendarCompany | null;
  suggestions: Array<CalendarCompany & { matches: number }>;
  taskId: string | null;
  dueDate?: string | null;
  user?: { id: string; fullName: string };
  comments?: Array<{ id: string; body: string; author: string; createdAt: string }>;
}
interface EventPage { events: CalendarEvent[]; hasNext: boolean; page: number; }

@Injectable({ providedIn: 'root' })
export class CalendarService {
  readonly openDetails = new Subject<string>();
  private readonly url = environment.apiUrl + '/api/calendar';
  constructor(private readonly http: HttpClient) {}

  connections(): Observable<{ connections: CalendarConnection[] }> {
    return this.http.get<{ connections: CalendarConnection[] }>(this.url + '/connections', { context: new HttpContext().set(SKIP_SPINNER, true) });
  }
  readonly openAssociation = new Subject<string>();
  readonly validateEvent = new Subject<string>();
  taskAction = new Subject<{id:string; action:'edit'|'reset'|'delete'|'times'|'notApplicable'}>();
  readonly unlinkCompany = new Subject<string>();
  readonly openComments = new Subject<string>();
  authorize(provider: CalendarProvider): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(this.url + '/' + provider + '/authorize', {});
  }
  calendars(provider: CalendarProvider): Observable<{ calendars: ExternalCalendar[] }> {
    return this.http.get<{ calendars: ExternalCalendar[] }>(this.url + '/' + provider + '/calendars');
  }
  configure(provider: CalendarProvider, calendarIds: string[], defaultTag: string): Observable<unknown> {
    return this.http.post(this.url + '/' + provider + '/configure', { calendarIds, defaultTag: defaultTag || null });
  }
  color(provider: CalendarProvider, color: string): Observable<{ color: string }> {
    return this.http.patch<{ color: string }>(this.url + '/' + provider + '/color', { color });
  }
  action(provider: CalendarProvider, action: 'sync' | 'disconnect'): Observable<unknown> {
    return this.http.post(this.url + '/' + provider + '/' + action, {});
  }
  events(from: string, until: string, unclassified: boolean, overdue = false): Observable<CalendarEvent[]> {
    const page = (index: number) => this.http.get<Omit<EventPage, 'page'>>(this.url + '/events', {
      context: new HttpContext().set(SKIP_SPINNER, true),
      params: new HttpParams().set('from', from).set('until', until).set('unclassified', unclassified).set('page', index).set('overdue', overdue),
    }).pipe(map(result => ({ ...result, page: index })));
    return page(1).pipe(
      expand(result => result.hasNext ? page(result.page + 1) : EMPTY),
      reduce((events, result) => [...events, ...result.events], [] as CalendarEvent[])
    );
  }
  qualify(id: string, update: { companyId?: string | null; status?: string; time?: {startTime:string; duration:number; description:string}; reset?: boolean; comment?: string; excluded?: boolean; planning?: { startsAt: string; endsAt: string; allDay: boolean } }): Observable<CalendarEvent> {
    return this.http.patch<CalendarEvent>(this.url + '/events/' + id, update);
  }
  event(id: string): Observable<CalendarEvent> {
    return this.http.get<CalendarEvent>(this.url + '/events/' + id);
  }
}
